import React from 'react';
import { AlertCircle, ArrowDown, ArrowRight, Eye, Lock, Scale, Zap } from 'lucide-react';
import ReportPreviewCard, { ExempleRapportBadge, MentionFictif } from './ReportPreviewCard';

/**
 * Hero de la landing — planches 1 (desktop 1440) et 2 (mobile 390) de la maquette
 * « Refonte conversion Anti-Recouvrement ».
 *
 * Deux intentions portées par la maquette, à ne pas défaire :
 *  - la ligne de flottaison tombe sur la barre de chiffres, plus sur du vide ;
 *  - la carte de rapport est visible dès l'arrivée : le visiteur voit le produit
 *    avant de déposer quoi que ce soit.
 *
 * Les chiffres 154 et 73 % viennent de l'admin. Ne pas les retoucher sans
 * réactualiser la source.
 */

const REASSURANCE = [
  { icon: Lock, color: '#16a34a', court: 'Confidentiel', long: 'Dossier confidentiel' },
  { icon: Scale, color: '#1e3a8a', court: 'Droit français', long: 'Droit français' },
  { icon: Zap, color: '#d97706', court: '3 minutes', long: 'Réponse en 3 minutes' },
];

const CHIFFRES = [
  { valeur: '154', libelle: 'dossiers analysés' },
  { valeur: '73 %', libelle: 'présentaient une irrégularité' },
  { valeur: '0 €', libelle: 'pour le diagnostic complet' },
];

const Temoignage = () => (
  <div className="flex items-start gap-[11px] sm:gap-[13px]">
    <div
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-sm font-bold sm:h-[38px] sm:w-[38px] sm:text-[15px]"
      style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}
    >
      K
    </div>
    <div className="flex flex-col gap-[2px] sm:gap-[3px]">
      <div className="text-[13.5px] leading-normal sm:text-[15.5px]" style={{ color: '#1e293b' }}>
        «&nbsp;Après le courrier de demande de pièces, je n'ai plus jamais eu de nouvelles
        d'eux&nbsp;[les huissiers].&nbsp;»
      </div>
      <div className="text-xs sm:text-[13px]" style={{ color: '#64748b' }}>
        Karine · Concarneau · saisie attribution
      </div>
    </div>
  </div>
);

const HeroSection = ({ onCTAClick, onVoirExemple }) => (
  <section className="bg-white">
    <div className="mx-auto max-w-[1440px] px-[18px] pt-4 sm:px-6 sm:pt-10 lg:px-[72px]">
      <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-16">
        {/* Colonne gauche */}
        <div className="flex flex-col gap-3 sm:gap-5 lg:pt-2">
          <div
            className="inline-flex select-none items-center gap-[7px] self-start rounded-full border px-3 py-[6px] text-xs font-semibold sm:gap-2 sm:px-[14px] sm:py-[7px] sm:text-[13px]"
            style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}
          >
            <AlertCircle size={14} strokeWidth={2.3} className="shrink-0" />
            <span>
              Ne payez rien<span className="hidden sm:inline">, ne signez rien</span> avant d'avoir
              vérifié
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="m-0 text-[31px] font-extrabold leading-[1.1] tracking-tight sm:text-[40px] lg:text-[50px] lg:leading-[1.04]"
            style={{ color: '#1e3a8a', textWrap: 'balance' }}
          >
            Lettre de recouvrement&nbsp;? Vérifiez si vous devez vraiment payer
          </h1>

          <p
            className="m-0 max-w-[520px] text-[15.5px] leading-relaxed sm:text-[19px]"
            style={{ color: '#374151', textWrap: 'pretty' }}
          >
            Déposez vos documents. En 3 minutes, vous saurez si la dette semble{' '}
            <strong className="font-semibold" style={{ color: '#1e293b' }}>prescrite</strong>
            <span className="hidden sm:inline">, si la procédure est </span>
            <span className="sm:hidden"> et si la procédure est </span>
            <strong className="font-semibold" style={{ color: '#1e293b' }}>contestable</strong>
            <span className="hidden sm:inline">, et ce que vous devez faire maintenant</span>.
          </p>

          <div className="mt-[2px] flex flex-col items-stretch gap-[9px] sm:mt-1 sm:items-start sm:gap-3">
            <button
              type="button"
              onClick={onCTAClick}
              data-testid="hero-cta"
              className="inline-flex h-14 items-center justify-center gap-[10px] rounded-full px-8 text-[16.5px] font-semibold text-white transition-transform active:scale-[0.99] sm:h-auto sm:gap-3 sm:px-[34px] sm:py-5 sm:text-[18px]"
              style={{
                backgroundColor: '#1e3a8a',
                boxShadow:
                  '0 10px 15px -3px rgba(30, 58, 138, 0.28), 0 4px 6px -4px rgba(30, 58, 138, 0.2)',
              }}
            >
              {/* « — gratuit » saute sous sm : la pilule passait sur deux lignes,
                  et la ligne juste dessous le redit déjà. */}
              <span className="sm:hidden">Analyser mes documents</span>
              <span className="hidden sm:inline">Analyser mes documents — gratuit</span>
              <ArrowRight size={19} strokeWidth={2.2} />
            </button>
            <div className="text-center text-[12.5px] sm:pl-1 sm:text-left sm:text-sm" style={{ color: '#6b7280' }}>
              <span className="sm:hidden">Gratuit · Sans engagement · Sans carte bancaire</span>
              <span className="hidden sm:inline">
                Résultat immédiat · Sans engagement · Aucune carte bancaire
              </span>
            </div>

            {/* Volontairement discret : il ne doit pas concurrencer le dépôt. */}
            <button
              type="button"
              onClick={onVoirExemple}
              data-testid="hero-voir-exemple"
              className="inline-flex items-center justify-center gap-2 self-center rounded-full border px-5 py-[10px] text-[14px] font-medium transition-colors hover:bg-slate-50 sm:self-start"
              style={{ borderColor: '#cbd5e1', color: '#1e3a8a' }}
            >
              <Eye size={16} />
              À quoi ressemblerait mon analyse&nbsp;?
            </button>
          </div>

          <div className="pt-1 sm:mt-[6px] sm:pt-0">
            <Temoignage />
          </div>

          <div
            className="mt-[2px] flex justify-between border-t pt-3 sm:mt-1 sm:justify-start sm:gap-[26px] sm:pt-5"
            style={{ borderColor: '#e5e7eb' }}
          >
            {REASSURANCE.map(({ icon: Icon, color, court, long }) => (
              <div
                key={court}
                className="flex items-center gap-[6px] text-xs font-medium sm:gap-2 sm:text-[13px]"
                style={{ color: '#4b5563' }}
              >
                <Icon size={16} strokeWidth={2.1} className="shrink-0" style={{ color }} />
                <span className="sm:hidden">{court}</span>
                <span className="hidden sm:inline">{long}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Colonne droite : le produit, visible dès l'arrivée */}
        <div className="relative lg:pt-[6px]">
          {/* Mobile : séparateur + mention fictif au-dessus de la carte, pour qu'elle
              reste lisible même quand le bas de la carte passe sous la ligne de flottaison. */}
          <div className="lg:hidden">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-px flex-grow" style={{ backgroundColor: '#e2e8f0' }} />
              <ExempleRapportBadge />
              <div className="h-px flex-grow" style={{ backgroundColor: '#e2e8f0' }} />
            </div>
            <MentionFictif className="mb-3" />
          </div>

          <ExempleRapportBadge className="absolute -top-2 right-[6px] z-10 hidden lg:inline-flex" />

          <ReportPreviewCard compact />

          <MentionFictif className="mt-[14px] hidden lg:flex" />
        </div>
      </div>
    </div>

    {/* Barre de chiffres : c'est elle qui doit tomber sur la ligne de flottaison,
        et l'indice de scroll qui annonce la suite. */}
    <div
      className="mt-8 border-t sm:mt-10"
      style={{ backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }}
    >
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4 px-[18px] py-5 sm:px-6 sm:py-[22px] lg:px-[72px]">
        <div className="flex flex-grow justify-between gap-6 sm:flex-grow-0 sm:justify-start sm:gap-[52px]">
          {CHIFFRES.map(({ valeur, libelle }) => (
            <div key={valeur} className="flex flex-col gap-[2px]">
              <div className="text-xl font-extrabold leading-tight sm:text-2xl" style={{ color: '#1e3a8a' }}>
                {valeur}
              </div>
              <div className="text-[11.5px] sm:text-[12.5px]" style={{ color: '#64748b' }}>{libelle}</div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onCTAClick}
          className="hidden items-center gap-[9px] text-[13px] font-medium lg:flex"
          style={{ color: '#64748b' }}
        >
          Déposer mes documents
          <ArrowDown size={16} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  </section>
);

export default HeroSection;
